import { useEffect, useState } from "react";
import { Loader2, X, Pencil, MapPin, ShieldAlert, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { staffApi, fmtErr } from "../api";

// Admin → Properties & Geofencing (SuperAdmin only).
// Branch radius is INDEPENDENT per property (backend validates 0 < radius_m ≤ 5000).
// Auto-attendance is an office-level setting exposed by the backend as
// attendance_login_mode (auto|manual) + background tracking. Per-branch OFF/SHADOW/ENFORCE
// modes are NOT exposed by the live backend — only real backend fields are shown/edited.
const Properties = () => {
  const [branches, setBranches] = useState([]);
  const [office, setOffice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // branch object
  const [form, setForm] = useState({ name: "", latitude: "", longitude: "", radius_m: "", active: true });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [editingOffice, setEditingOffice] = useState(false);
  const [officeForm, setOfficeForm] = useState({ attendance_login_mode: "auto", background_tracking_enabled: false, background_ping_interval_min: 15 });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [b, o] = await Promise.allSettled([staffApi.get("/branches"), staffApi.get("/config/office")]);
      if (b.status === "fulfilled") setBranches(Array.isArray(b.value.data) ? b.value.data : []);
      else setError(fmtErr(b.reason));
      if (o.status === "fulfilled") setOffice(o.value.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (br) => {
    setForm({ name: br.name || "", latitude: String(br.latitude ?? ""), longitude: String(br.longitude ?? ""), radius_m: String(br.radius_m ?? ""), active: br.active !== false });
    setFormError("");
    setEditing(br);
  };

  const submitBranch = async (e) => {
    e.preventDefault();
    setFormError("");
    const radius = parseFloat(form.radius_m);
    if (!(radius > 0) || radius > 5000) { setFormError("Radius must be between 1 and 5000 metres (backend limit)."); return; }
    if (!window.confirm(`Update geofence for "${editing.name}"?\n\nRadius: ${radius}m · Centre: ${form.latitude}, ${form.longitude}\n\nThis changes where staff can clock in at this property.`)) return;
    setBusy(true);
    try {
      await staffApi.patch(`/branches/${editing.id}`, {
        name: form.name.trim(),
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_m: radius,
        active: form.active,
      });
      toast.success("Property geofence updated.");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const openOfficeEdit = () => {
    setOfficeForm({
      attendance_login_mode: office?.attendance_login_mode || "auto",
      background_tracking_enabled: !!office?.background_tracking_enabled,
      background_ping_interval_min: office?.background_ping_interval_min ?? 15,
    });
    setFormError("");
    setEditingOffice(true);
  };

  const submitOffice = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!window.confirm(
      "WARNING — this changes automatic attendance behaviour for ALL staff.\n\n" +
      `Mode: ${officeForm.attendance_login_mode.toUpperCase()} · Background tracking: ${officeForm.background_tracking_enabled ? "ON" : "OFF"}\n\n` +
      "Proceed?"
    )) return;
    setBusy(true);
    try {
      await staffApi.patch("/config/office", {
        attendance_login_mode: officeForm.attendance_login_mode,
        background_tracking_enabled: officeForm.background_tracking_enabled,
        background_ping_interval_min: parseInt(officeForm.background_ping_interval_min, 10) || 15,
      });
      toast.success("Auto-attendance settings updated.");
      setEditingOffice(false);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const center = branches.length ? [branches[0].latitude, branches[0].longitude] : [19.076, 72.8777];
  const bounds = branches.length ? branches.map((b) => [b.latitude, b.longitude]) : null;
  const inputCls = "w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="properties-page">
      <h1 className="font-display text-2xl font-bold text-slate-900">Properties & Geofencing</h1>
      <p className="mt-1 text-sm text-slate-500">Each property has its own independent geofence radius (backend limit: 5000 m). Changes affect where staff can clock in.</p>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="props-error">{error}</p>
      ) : (
        <>
          {/* map */}
          {branches.length > 0 && (
            <div className="mt-6 bg-white border border-slate-200 overflow-hidden" data-testid="props-map-card">
              <MapContainer {...(bounds ? { bounds, boundsOptions: { padding: [50, 50] } } : { center, zoom: 13 })} style={{ height: 380, width: "100%" }} data-testid="props-map">
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {branches.map((b) => (
                  <span key={b.id}>
                    <Circle center={[b.latitude, b.longitude]} radius={b.radius_m} pathOptions={{ color: "#C5221F", fillColor: "#C5221F", fillOpacity: 0.08, weight: 2 }} />
                    <CircleMarker center={[b.latitude, b.longitude]} radius={7} pathOptions={{ color: "#0A1128", fillColor: "#0A1128", fillOpacity: 1 }}>
                      <Popup>
                        <strong>{b.name}</strong><br />Radius: {b.radius_m} m<br />{b.latitude.toFixed(5)}, {b.longitude.toFixed(5)}
                      </Popup>
                    </CircleMarker>
                  </span>
                ))}
              </MapContainer>
            </div>
          )}

          {/* branches table */}
          <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="props-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {["Property", "Latitude", "Longitude", "Radius", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {branches.map((b) => (
                  <tr key={b.id} data-testid={`props-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800 flex items-center gap-2"><MapPin size={13} className="text-[#C5221F]" /> {b.name}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{b.latitude?.toFixed(5)}</td>
                    <td className="px-4 py-3 text-slate-600">{b.longitude?.toFixed(5)}</td>
                    <td className="px-4 py-3" data-testid={`props-radius-${b.id}`}>
                      <span className="inline-block border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-slate-700">{b.radius_m} m</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${b.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {b.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(b)} data-testid={`props-edit-${b.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline">
                        <Pencil size={11} /> Edit Geofence
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* auto attendance safety */}
          {office && (
            <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="auto-attendance-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                    <Settings2 size={14} className="text-[#C5221F]" /> Auto Attendance (Office Level)
                  </p>
                  <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Attendance Mode</p>
                      <p className="mt-1 font-display font-bold text-slate-900 uppercase" data-testid="office-mode">{office.attendance_login_mode || "auto"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Background Tracking</p>
                      <p className="mt-1 font-display font-bold text-slate-900" data-testid="office-bg">{office.background_tracking_enabled ? `On · every ${office.background_ping_interval_min}m` : "Off"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Office Hours</p>
                      <p className="mt-1 font-display font-bold text-slate-900">{office.in_time} – {office.out_time} <span className="text-slate-400 font-normal">(grace {office.in_grace_min}m)</span></p>
                    </div>
                  </div>
                  <p className="mt-4 text-[11px] text-slate-400 flex items-start gap-1.5">
                    <ShieldAlert size={12} className="text-amber-500 shrink-0 mt-0.5" />
                    Per-branch automation modes (OFF / SHADOW / ENFORCE) are managed in the attendance backend and are not editable here. Changes below apply office-wide and always require explicit confirmation.
                  </p>
                </div>
                <button onClick={openOfficeEdit} data-testid="office-edit-btn"
                  className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] hover:text-[#C5221F] text-slate-700 px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
                  <Pencil size={11} /> Edit Settings
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* branch edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid="props-form-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">Edit Geofence — {editing.name}</h2>
              <button onClick={() => setEditing(null)} data-testid="props-form-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submitBranch} className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Property Name *</label>
                <input required data-testid="props-form-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Latitude *</label>
                  <input required type="number" step="any" data-testid="props-form-lat" className={inputCls} value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Longitude *</label>
                  <input required type="number" step="any" data-testid="props-form-lng" className={inputCls} value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Radius (metres, max 5000) *</label>
                <input required type="number" min="1" max="5000" data-testid="props-form-radius" className={inputCls} value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} />
                <p className="mt-1 text-[11px] text-slate-400">Independent per property — no global limit is applied.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="props-form-active" className="w-4 h-4 accent-[#C5221F]" />
                Active (staff can clock in at this property)
              </label>
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="props-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="props-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Save Geofence
              </button>
            </form>
          </div>
        </div>
      )}

      {/* office settings modal */}
      {editingOffice && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid="office-form-modal" onClick={() => setEditingOffice(false)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg font-bold text-slate-900">Auto Attendance Settings</h2>
            <p className="mt-1 text-xs text-slate-500">Office-wide. Applies to every property and every employee.</p>
            <form onSubmit={submitOffice} className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Attendance Mode</label>
                <select data-testid="office-form-mode" className={inputCls} value={officeForm.attendance_login_mode} onChange={(e) => setOfficeForm({ ...officeForm, attendance_login_mode: e.target.value })}>
                  <option value="auto">AUTO — app records attendance automatically within geofence</option>
                  <option value="manual">MANUAL — staff clock in/out themselves</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={officeForm.background_tracking_enabled} onChange={(e) => setOfficeForm({ ...officeForm, background_tracking_enabled: e.target.checked })} data-testid="office-form-bg" className="w-4 h-4 accent-[#C5221F]" />
                Background GPS tracking
              </label>
              {officeForm.background_tracking_enabled && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Ping Interval (minutes, 5–120)</label>
                  <input type="number" min="5" max="120" data-testid="office-form-interval" className={inputCls} value={officeForm.background_ping_interval_min} onChange={(e) => setOfficeForm({ ...officeForm, background_ping_interval_min: e.target.value })} />
                </div>
              )}
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="office-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="office-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Save (Requires Confirmation)
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Properties;
